import { createStore, mixStores, StoreCore, validateAll } from "@priolo/jon"
import cropsRecipesApi from "api/crops_recipes"
import i18n from "i18next"
import { CROP_RECIPE_VIEW } from "pages/cropsRecipes/detail/Header"
import dialogSo, { DIALOG_TYPE } from "stores/layout/dialogStore"
import cropRecipeDaySo from "stores/library/cropRecipe/detail"
import querySo from "stores/route/query"
import { URL_PAR } from "stores/route/utils/types"
import { CropRecipe } from "types/CropRecipe"
import { Recipe } from "types/Recipe"
import { delay } from "utils"
import { rules } from "utils/rules"
import folderSetup from "../folder"
import { getNewRecipe } from "../recipe/utils"
import { ExtendedGroupPhase } from "./types"
import { cropPhasesGroupToDays, daysToPhasesGroup, isRealPhase, recipePhasesGroupToDays } from "./utils"
import { Uuid } from "types/global"



/**
 * Gestisce la lista dei CROP
 */
const setup = {

	state: {
		all: <CropRecipe[]>[],
		// CROP in editazione
		select: <CropRecipe>null,
		/** 
		 * i custom paramenter in editazione. 
		 * devo fare un array perche' un dictionary è difficilmente gestibile come lista editabile
		 * */
		selectCustomFields: <[string, string][]>null,
	},

	getters: {
		getByUuid: (uuid: Uuid, store?: CropRecipeStore): CropRecipe => uuid ? store.state.all.find(c => c.uuid == uuid) : null,
		getById: (id: number, store?: CropRecipeStore): CropRecipe => store.state.all.find(c => c.id == id),
		getByRecipeId : (recipeId: number, store?: CropRecipeStore): CropRecipe => 
			!!recipeId ? store.state.all.find(c => c.recipeId == recipeId) : null,
		isSelected: (id: number, store?: CropRecipeStore): boolean => {
			if (id == null) return false
			return store.state.selectedIds.includes(id)
		},
	},

	actions: {
		/** carico tutti i CROP-RECIPE */
		fetchAll: async (_: void, store?: CropRecipeStore) => {
			const { data } = await cropsRecipesApi.index()
			store.setAll(data)
			return data
		},
		fetchAllIfVoid: async (_: void, store?: CropRecipeStore) => {
			if (store.state.all?.length > 0) return
			return await store.fetchAll()
		},

		/** carico un CROP-RECIPE e lo metto in EDIT*/
		fetchSelect: async (id: number, store?: CropRecipeStore) => {
			const { data } = await cropsRecipesApi.get(id)
			store.updateSelect(data)
		},
		/** clono un CROP-RECIPE e lo metto in EDIT*/
		fetchSelectAndClone: async (id: number, store?: CropRecipeStore) => {
			const { data } = await cropsRecipesApi.get(id)
			delete data.id
			delete data.uuid
			delete data.recipeId
			delete data.recipe?.id
			delete data.recipe?.uuid
			store.updateSelect(data)
		},
		updateSelect: (cropRecipe: CropRecipe, store?: CropRecipeStore) => {
			const groupsDays = daysToPhasesGroup(cropRecipe)
			store.setSelect(cropRecipe)
			cropRecipeDaySo.setDaysGroups(groupsDays as ExtendedGroupPhase[])
			cropRecipeDaySo.setCommon((groupsDays.find(g => isRealPhase(g.phase))?.days[0]) ?? {})
		},
		/** crea un nuovo CROP-RECIPE e lo mette in editazione (select) */
		editNew: async (_: void, store?: CropRecipeStore) => {
			const newRecipe = getNewRecipe()
			store.setSelect({
				groupingFolderId: store.state.selectedFolderId,
				name: "", farmIds: [], variety: "", vendor: "",
				seedDensity: 0, expectedYieldPerMq: 0, cost1kg: 0,
				seedCostPerMillion: 0, pillsTypology: "", seedsPillsPerHdTray: 0,
				rearrangementSchema: [], days: [],
				recipe: newRecipe,
			})
			cropRecipeDaySo.setNewDaysGroups()
		},




		/** salvo il CROP-RECIPE in EDIT */
		save: async (cropRecipe: CropRecipe, store?: CropRecipeStore) => {
			// rivalorizzo i custom fields del CROP
			cropRecipe.customFields = store.state.selectCustomFields.reduce((acc, [key, value]) => {
				if (key.trim().length == 0) return acc
				acc[key] = value;
				return acc;
			}, {} as { [key: string]: string })
			// salvataggio sul BE
			const { data } = await cropsRecipesApi.save(cropRecipe.id, cropRecipe)
			store.state.selectCustomFields = null
			store.setSelect(data)
		},
		destroy: async (id: number, store?: CropRecipeStore) => {
			const res = await dialogSo.dialogOpen({
				type: DIALOG_TYPE.WARNING,
				text: "Are you sure you want to delete the CROP?",
			})
			if (!res) return
			await cropsRecipesApi.destroy(id)
			dialogSo.dialogOpen({
				type: DIALOG_TYPE.INFO, modal: false,
				text: "THE CROP HAS BEEN DELETED",
			})
			const crops = store.state.all.filter(i => i.id != id)
			store.setAll(crops)
		},
		archive: async (id: number, store?: CropRecipeStore) => {
			const res = await dialogSo.dialogOpen({
				type: DIALOG_TYPE.WARNING,
				text: i18n.t("dialog.crop-recipe.archive.confirm"),
			})
			if (res == false) return

			const { data: cropArchived } = await cropsRecipesApi.archive(id)
			const crop = store.state.all.find(crop => crop.id == cropArchived.id)
			crop.archived = true
			store.setAll([...store.state.all])

			dialogSo.dialogOpen({
				type: DIALOG_TYPE.INFO, modal: false,
				text: i18n.t("snackbar.crop-recipe.archive"),
			})
		},
		bulkArchive: async (_: void, store?: CropRecipeStore) => {
			if (store.state.selectedIds.length == 0) return
			if (await dialogSo.dialogOpen({
				type: DIALOG_TYPE.WARNING,
				text: i18n.t("dialog.crop-recipe.bulk-archive.confirm"),
			}) == false) return

			await cropsRecipesApi.bulkArchive(store.state.selectedIds)
			for (const crop of store.state.all) if (store.state.selectedIds.includes(crop.id)) crop.archived = true
			store.setAll([...store.state.all])
			store.setSelectedIds([])

			dialogSo.dialogOpen({
				type: DIALOG_TYPE.INFO, modal: false,
				text: i18n.t("snackbar.crop-recipe.bulk-archive"),
			})

		},
		restore: async (id: number, store?: CropRecipeStore) => {
			const { data: cropArchived } = await cropsRecipesApi.restore(id)
			dialogSo.dialogOpen({
				type: DIALOG_TYPE.INFO, modal: false,
				text: i18n.t("snackbar.crop-recie.restore"),
			})
			const crop = store.state.all.find(crop => crop.id == cropArchived.id)
			crop.archived = false
			store.setAll([...store.state.all])
		},
		/** verifica e salva il CROP in EDIT  */
		saveSelect: async (_: void, store?: CropRecipeStore) => {

			//#region VERIFICA

			// riprisitno la visualizzazione di tutti i componenti
			if (querySo.getSearchUrl([URL_PAR.CROP_RECIPE_VIEW])[0] == CROP_RECIPE_VIEW.NO_PROPS) {
				querySo.setSearchUrl([URL_PAR.CROP_RECIPE_VIEW, null])
				await delay(300)
			}

			// validazione dei dati inseriti
			if (validateAll().length > 0) {
				dialogSo.dialogOpen({
					modal: false, type: DIALOG_TYPE.WARNING,
					text: "There are some incorrect values. Please check and try saving again."
				})
				return false
			}
			// controllo che il nome sia presente e sia settata almeno una FARM
			if (rules.obligatory(store.state.select.name) || rules.obligatoryArray(store.state.select.farmIds)) {
				return false
			}
			// controllo che il ratio sia corretto
			if (!cropRecipeDaySo.checkAllRatio()) {
				dialogSo.dialogOpen({
					modal: false, type: DIALOG_TYPE.WARNING,
					text: i18n.t("dialog.crop-recipe.ratio.text")
				})
				return false
			}

			//#endregion VERIFICA


			// RECIPE
			const { days: recipeDays, shrinkExpandDays } = recipePhasesGroupToDays(
				cropRecipeDaySo.state.daysGroups,
				cropRecipeDaySo.state.common
			)
			// controllo che il photoperiod non sia presente
			if (recipeDays.some(day => rules.photoperiod([day.dayPhotoPeriod, day.nightPhotoPeriod]) != null)) {
				return false
			}
			const newRecipe: Recipe = {
				...store.state.select.recipe,
				days: recipeDays,
				shrinkExpandDays,
				name: store.state.select.name,
				autotagList: store.state.select.autotagList,
				farmIds: store.state.select.farmIds,
				groupingFolderId: store.state.select.groupingFolderId,
			}

			// CROP
			const { days: cropDays, rearrangementSchema } = cropPhasesGroupToDays(cropRecipeDaySo.state.daysGroups)
			const newCropRecipe: CropRecipe = {
				...store.state.select,
				days: cropDays,
				rearrangementSchema,
				recipe: newRecipe,
				recipeId: newRecipe.id,
			}

			// salvataggio sul BE
			await store.save(newCropRecipe)
			dialogSo.dialogOpen({
				type: DIALOG_TYPE.INFO, modal: false,
				text: i18n.t(`snackbar.crop-recipe.${store.state.select.id ? "update" : "create"}`)
			})
			return true
		},
	},

	mutators: {
		setAll: (all: CropRecipe[]) => ({ all }),
		setSelect: (select: CropRecipe) => ({
			select,
			selectCustomFields: Object.entries(select?.customFields ?? {})
		}),
		setSelectCustomFields: (selectCustomFields: [string, string][]) => ({ selectCustomFields }),
		setSelectProp: (prop: Partial<CropRecipe>, store?: CropRecipeStore) => ({ select: { ...store.state.select, ...prop } }),
	},

}

export type CropRecipeState = typeof setup.state & typeof folderSetup.state
export type CropRecipeGetters = typeof setup.getters & typeof folderSetup.getters
export type CropRecipeActions = typeof setup.actions & typeof folderSetup.actions
export type CropRecipeMutators = typeof setup.mutators & typeof folderSetup.mutators
export interface CropRecipeStore extends StoreCore<CropRecipeState>, CropRecipeGetters, CropRecipeActions, CropRecipeMutators {
	state: CropRecipeState
}
const cropRecipeSo = createStore(mixStores(folderSetup, setup)) as CropRecipeStore
cropRecipeSo.state.repo = "crops_recipes"

export default cropRecipeSo

